import { useEffect } from "react";
import PeachtreeCornersHeroSection from "./components/HeroSection";
import PeachtreeCornersStickyNav from "./components/StickyNav";
import PeachtreeCornersProcessingTimeSection from "./components/ProcessingTimeSection";
import PeachtreeCornersServicesSection from "./components/ServicesSection";
import PeachtreeCornersLocalAreaSection from "./components/LocalAreaSection";
import PeachtreeCornersTestimonialsSection from "./components/TestimonialsSection";
import PeachtreeCornersAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import PeachtreeCornersComparisonSection from "./components/ComparisonSection";
import PeachtreeCornersMistakesSection from "./components/MistakesSection";
import PeachtreeCornersHowItWorksSection from "./components/HowItWorksSection";
import PeachtreeCornersFAQSection, { faqs } from "./components/FAQSection";
import PeachtreeCornersHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import PeachtreeCornersFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function PeachtreeCornersPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Peachtree Corners GA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/peachtree-corners/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Peachtree Corners, GA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving western Gwinnett County travelers. Get your emergency passport fast!");
    setMeta("geo.placename", "Peachtree Corners, Georgia");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "33.9698;-84.2216");
    setMeta("ICBM", "33.9698, -84.2216");
    setMeta("keywords", "expedited passport, emergency passport, Peachtree Corners passport, same day passport Atlanta, fast passport Georgia");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia", item: "https://fastpassportcenter.com/georgia/" },
          { "@type": "ListItem", position: 3, name: "Peachtree Corners Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Peachtree Corners — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Peachtree Corners and western Gwinnett County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Peachtree Corners",
          containedInPlace: {
            "@type": "State",
            name: "Georgia",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Peachtree Corners",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <PeachtreeCornersHeroSection />
      <PeachtreeCornersStickyNav />
      <PeachtreeCornersProcessingTimeSection />
      <PeachtreeCornersServicesSection />
      <PeachtreeCornersLocalAreaSection />
      <PeachtreeCornersTestimonialsSection />
      <PeachtreeCornersAcceptanceFacilitiesSection />
      <PeachtreeCornersComparisonSection />
      <PeachtreeCornersMistakesSection />
      <PeachtreeCornersHowItWorksSection />
      <PeachtreeCornersFAQSection />
      <PeachtreeCornersHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/peachtree-corners" state="GA" />
      <PeachtreeCornersFinalCTA />
    </div>
  );
}